/**
 * CSRF Token Management Utility
 *
 * Handles fetching and storing CSRF tokens for API requests.
 * The server uses double CSRF protection with csrf-csrf library.
 *
 * How it works:
 * 1. Fetch token from /api/csrf-token endpoint
 * 2. Server sets HttpOnly cookie __Host-psifi.x-csrf-token
 * 3. Client stores token in memory
 * 4. Client sends token in x-csrf-token header for POST/PUT/PATCH/DELETE
 */

import { logger } from './logger';

let csrfToken: string | null = null;
let tokenFetchPromise: Promise<string> | null = null;

/**
 * Fetch CSRF token from server
 * Uses singleton pattern to prevent multiple concurrent requests
 */
export async function fetchCsrfToken(): Promise<string> {
    // If we're already fetching, wait for that request
    if (tokenFetchPromise) {
        return tokenFetchPromise;
    }

    // If we already have a token, return it
    if (csrfToken) {
        return csrfToken;
    }

    // Fetch new token
    tokenFetchPromise = fetch('/api/csrf-token', {
        method: 'GET',
        credentials: 'include', // Required to receive cookie
        headers: {
            Accept: 'application/json',
        },
    })
        .then(async response => {
            if (!response.ok) {
                throw new Error(`Failed to fetch CSRF token: ${response.status}`);
            }
            const data = await response.json();
            csrfToken = data.csrfToken;
            return csrfToken as string;
        })
        .catch(error => {
            logger.error('Error fetching CSRF token', error);
            // Reset promise so we can retry
            tokenFetchPromise = null;
            throw error;
        })
        .finally(() => {
            // Clear promise after completion
            tokenFetchPromise = null;
        });

    return tokenFetchPromise;
}

/**
 * Get current CSRF token from memory
 * Returns null if token hasn't been fetched yet
 */
export function getCsrfToken(): string | null {
    return csrfToken;
}

/**
 * Clear stored CSRF token
 * Useful for logout or when token becomes invalid
 */
export function clearCsrfToken(): void {
    csrfToken = null;
    tokenFetchPromise = null;
}

/**
 * Refresh CSRF token
 * Forces a new token fetch even if one exists
 */
export async function refreshCsrfToken(): Promise<string> {
    clearCsrfToken();
    return fetchCsrfToken();
}

/**
 * Initialize CSRF token on app load
 * Should be called in app initialization
 */
export async function initializeCsrf(): Promise<void> {
    try {
        await fetchCsrfToken();
        logger.info('CSRF token initialized successfully');
    } catch (error) {
        logger.error('Failed to initialize CSRF token:', error);
        // Don't throw - app should still work in development where CSRF is disabled
    }
}
